import { Injectable, inject } from '@angular/core';
import { queryOptions } from '@tanstack/angular-query-experimental';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { BackendService } from './backend.service';
import { recurringTransactionQueryKeys } from './query-keys';

export type RecurringTransactionClassification = 'SUBSCRIPTION' | 'BILL' | 'UNKNOWN';
export type RecurringTransactionDirection = 'EXPENSE' | 'INCOME';
export type RecurringTransactionKind = 'SUBSCRIPTION' | 'BILL' | 'PAYCHECK' | 'UNKNOWN';
export type RecurringTransactionCadence =
  | 'WEEKLY'
  | 'EVERY_TWO_WEEKS'
  | 'SEMI_MONTHLY'
  | 'MONTHLY'
  | 'EVERY_TWO_MONTHS'
  | 'EVERY_SIX_MONTHS'
  | 'YEARLY';

export interface RecurringTransactionListItem {
  id: string;
  merchantIdentity: {
    name?: string;
    displayName: string;
    logoUrl?: string | null;
  };
  classification: RecurringTransactionClassification;
  direction: RecurringTransactionDirection;
  kind: RecurringTransactionKind;
  cadence: RecurringTransactionCadence;
  status: 'ACTIVE' | 'DELAYED_OR_MISSING' | 'INACTIVE';
  ignored: boolean;
  expectedDate: string;
  pendingTransactionIds?: string[];
  amountBehavior: 'FIXED' | 'VARIABLE';
  amountSummary: {
    expectedAmount: number | null;
    averageAmount: number | null;
    lastAmount: number | null;
    minAmount: number | null;
    maxAmount: number | null;
  };
  paymentSourceEvidence?: RecurringTransactionPaymentSourceEvidence[];
}

export interface RecurringTransactionPaymentSourceEvidence {
  plaidAccountId: string;
  name: string | null;
  mask: string | null;
  transactionIds: string[];
}

export interface RecurringMatchedTransaction {
  id: string;
  merchant: string;
  date: string;
  amount: number;
  netValue: number;
  bucketId?: string | null;
  createdByUserId?: string;
  deleted?: boolean;
  labelIds?: string[];
  type?: string;
  description?: string | null;
  merchantMeta: {
    displayName?: string | null;
    logoUrl?: string | null;
    originalName?: string | null;
  } | null;
  plaidAccount: {
    name?: string;
    nickname?: string | null;
    mask?: string | null;
    officialName?: string | null;
    institution?: { name?: string | null; logoUrl?: string | null } | null;
  } | null;
  bankMeta?: {
    pending?: boolean;
    personalFinanceCategory?: {
      primary: string | null;
      detailed: string | null;
      confidenceLevel: string | null;
      version: string | null;
    } | null;
    personalFinanceCategoryIconUrl?: string | null;
  } | null;
  suggestedBucketIds?: string[] | null;
  locked?: { value: boolean; reason?: string | null } | null;
}

export interface RecurringTransactionDetail extends RecurringTransactionListItem {
  matchedTransactionIds: string[];
  matchedTransactions: RecurringMatchedTransaction[];
}

export interface RecurringTransactionList {
  data: RecurringTransactionListItem[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface RecurringTransactionListRequest {
  pageNumber: number;
  pageSize: number;
  direction?: RecurringTransactionDirection;
}

export interface UpcomingRecurringCard {
  id: string;
  title: string;
  expectedDate: Date;
  daysUntilExpected: number;
  amount: number | null;
  amountRange: { min: number; max: number } | null;
  classification: RecurringTransactionClassification;
  direction: RecurringTransactionDirection;
  kind: RecurringTransactionKind;
}

@Injectable({ providedIn: 'root' })
export class RecurringTransactionService {
  private backend = inject(BackendService);

  upcomingCardsQueryOptions() {
    return queryOptions({
      queryKey: recurringTransactionQueryKeys.upcomingCards(),
      queryFn: () => this.fetchUpcomingCardsFromBackend(),
      staleTime: 0,
    });
  }

  listQueryOptions(request: RecurringTransactionListRequest) {
    return queryOptions({
      queryKey: recurringTransactionQueryKeys.list(request),
      queryFn: () => this.fetchListFromBackend(request),
      staleTime: 0,
    });
  }

  allListQueryOptions(pageSize: number) {
    return queryOptions({
      queryKey: recurringTransactionQueryKeys.allList(pageSize),
      queryFn: () => this.fetchAllFromBackend(pageSize),
      staleTime: 0,
    });
  }

  detailQueryOptions(recurringTransactionId: string) {
    return queryOptions({
      queryKey: recurringTransactionQueryKeys.detail(recurringTransactionId),
      queryFn: () => this.fetchDetailFromBackend(recurringTransactionId),
      staleTime: 0,
    });
  }

  private fetchDetailFromBackend(recurringTransactionId: string): Promise<RecurringTransactionDetail> {
    return firstValueFrom(
      this.backend.request<RecurringTransactionDetail>({
        type: 'get',
        apiRoute: `recurring-transactions/${recurringTransactionId}`,
      }),
    );
  }

  private fetchListFromBackend(request: RecurringTransactionListRequest): Promise<RecurringTransactionList> {
    return firstValueFrom(
      this.backend.request<RecurringTransactionList>({
        type: 'get',
        apiRoute: 'recurring-transactions',
        params: {
          pageNumber: request.pageNumber,
          pageSize: request.pageSize,
          ...(request.direction ? { direction: request.direction } : {}),
        },
      }),
    );
  }

  private async fetchAllFromBackend(pageSize: number): Promise<RecurringTransactionList> {
    const firstPage = await this.fetchListFromBackend({ pageNumber: 1, pageSize });
    const data = [...firstPage.data];

    for (let pageNumber = 2; pageNumber <= firstPage.totalPages; pageNumber += 1) {
      const page = await this.fetchListFromBackend({ pageNumber, pageSize });
      data.push(...page.data);
    }

    return {
      ...firstPage,
      data,
      hasNextPage: false,
    };
  }

  private fetchUpcomingCardsFromBackend(): Promise<UpcomingRecurringCard[]> {
    return firstValueFrom(
      this.backend.request<RecurringTransactionList>({
        type: 'get',
        apiRoute: 'recurring-transactions',
        params: {
          excludePendingMatched: true,
          expectedDateFrom: DateTime.local().toISODate(),
          pageSize: 10,
          status: 'ACTIVE',
        },
      }),
    ).then(response => this.toUpcomingCards(response.data));
  }

  private toUpcomingCards(items: RecurringTransactionListItem[]): UpcomingRecurringCard[] {
    const today = this.startOfDay(new Date());

    return items
      .filter(item => item.status === 'ACTIVE' && !item.ignored)
      .map(item => this.toUpcomingCard(item, today))
      .filter((card): card is UpcomingRecurringCard => !!card)
      .sort((left, right) => left.expectedDate.getTime() - right.expectedDate.getTime())
      .slice(0, 10);
  }

  private toUpcomingCard(item: RecurringTransactionListItem, today: Date): UpcomingRecurringCard | null {
    const expectedDate = this.parseDateOnly(item.expectedDate);
    if (!expectedDate) return null;

    return {
      id: item.id,
      title: item.merchantIdentity.displayName,
      expectedDate,
      daysUntilExpected: this.daysBetween(today, expectedDate),
      amount: this.getAmount(item),
      amountRange: this.getAmountRange(item),
      classification: item.classification,
      direction: item.direction,
      kind: item.kind,
    };
  }

  private getAmount(item: RecurringTransactionListItem): number | null {
    if (this.shouldUseAmountRange(item)) return null;

    return (
      item.amountSummary.expectedAmount ??
      item.amountSummary.averageAmount ??
      item.amountSummary.lastAmount ??
      item.amountSummary.maxAmount ??
      item.amountSummary.minAmount
    );
  }

  private getAmountRange(item: RecurringTransactionListItem): { min: number; max: number } | null {
    if (!this.shouldUseAmountRange(item)) return null;

    return {
      min: item.amountSummary.minAmount as number,
      max: item.amountSummary.maxAmount as number,
    };
  }

  private shouldUseAmountRange(item: RecurringTransactionListItem): boolean {
    return (
      item.direction === 'EXPENSE' &&
      item.kind === 'SUBSCRIPTION' &&
      item.amountBehavior === 'VARIABLE' &&
      item.amountSummary.expectedAmount == null &&
      item.amountSummary.minAmount != null &&
      item.amountSummary.maxAmount != null &&
      item.amountSummary.minAmount !== item.amountSummary.maxAmount
    );
  }

  private parseDateOnly(date: string): Date | null {
    const [year, month, day] = date.split('-').map(part => Number(part));
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private daysBetween(start: Date, end: Date): number {
    return Math.round((end.getTime() - start.getTime()) / 86_400_000);
  }
}
